import { useEffect, useRef, useState } from 'react'
import { healthApi } from '../../api/client'

type ServerState = 'checking' | 'awake' | 'asleep' | 'waking'

/**
 * Server status indicator + wake-up button.
 *
 * The Render free tier spins the backend down after inactivity, so the first
 * request after a while can take ~1 min (cold start) or time out. This badge
 * pings /health on load; if the server is asleep it offers a "Despertar"
 * button that keeps pinging until the server responds, so the user can wake
 * it while preparing their lists instead of having the real job fail.
 */
export default function ServerStatus() {
  const [state, setState] = useState<ServerState>('checking')
  const wakingRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    void healthApi.check().then((ok) => {
      if (!cancelled) setState(ok ? 'awake' : 'asleep')
    })
    return () => {
      cancelled = true
    }
  }, [])

  /** Poll /health until the server answers (or the user cancels). */
  const wake = () => {
    if (wakingRef.current) return
    wakingRef.current = true
    setState('waking')
    const attempt = async () => {
      // Keep trying until the server responds. Each ping nudges Render to
      // start the instance; the first success means it's awake.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const ok = await healthApi.check()
        if (ok) break
        await new Promise((r) => setTimeout(r, 4000))
      }
      wakingRef.current = false
      setState('awake')
    }
    void attempt()
  }

  if (state === 'awake') {
    return (
      <span
        className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1"
        title="El servidor está activo"
      >
        <span className="w-2 h-2 rounded-full bg-green-500" />
        Servidor activo
      </span>
    )
  }

  if (state === 'checking') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1">
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" />
        Comprobando…
      </span>
    )
  }

  if (state === 'waking') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
        Despertando servidor…
      </span>
    )
  }

  // asleep
  return (
    <button
      onClick={wake}
      className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1 hover:bg-red-100 transition-colors"
      title="El servidor está dormido. Pulsa para despertarlo mientras preparas las listas."
    >
      <span className="w-2 h-2 rounded-full bg-red-500" />
      Servidor apagado — Despertar
    </button>
  )
}
